import type * as React from 'react'
import { cn } from '@/helpers/utils'
import { ChevronLeftIcon, ChevronRightIcon, DotsHorizontalIcon } from './icons'

const makeButtonClassName = ({
	page,
	isActive
}: {
	page?: number
	isActive: boolean
}) =>
	cn(
		'flex h-8 items-center justify-center rounded-full font-medium text-grey-1000 text-sm/4.5 transition enabled:cursor-pointer',
		'focus-visible:shadow-airbnb-focus-visible focus-visible:outline-none',
		'disabled:cursor-not-allowed disabled:text-grey-500',
		// A single-digit page stays a circle; a wider label keeps the same
		// height and grows sideways instead of stretching into an oval.
		!page || page < 10 ? 'w-8' : 'min-w-8 px-2.5',
		isActive
			? 'bg-grey-1000 text-neutral-white'
			: 'hover:not-disabled:bg-grey-100'
	)

function Pagination({ className, ...props }: React.ComponentProps<'nav'>) {
	return (
		<nav
			aria-label='pagination'
			className={cn('mx-auto flex w-full justify-center', className)}
			data-slot='pagination'
			{...props}
		/>
	)
}

function PaginationContent({
	className,
	...props
}: React.ComponentProps<'ul'>) {
	return (
		<ul
			className={cn('flex flex-row items-center gap-4 py-2', className)}
			data-slot='pagination-content'
			{...props}
		/>
	)
}

function PaginationItem({ className, ...props }: React.ComponentProps<'li'>) {
	return (
		<li
			className={cn('flex items-center justify-center', className)}
			data-slot='pagination-item'
			{...props}
		/>
	)
}

type PaginationLinkProps = {
	isActive?: boolean
	page?: number
} & React.ComponentProps<'button'>

function PaginationLink({
	isActive = false,
	page,
	className,
	...props
}: PaginationLinkProps) {
	return (
		<button
			aria-current={isActive ? 'page' : undefined}
			className={cn(makeButtonClassName({ page, isActive }), className)}
			data-active={isActive}
			data-slot='pagination-link'
			type='button'
			{...props}
		/>
	)
}

// The arrows read as secondary next to the page numbers, so they carry their
// own grey rather than the numbers' near-black.
const ARROW_CLASS_NAME = 'text-grey-1000 hover:not-disabled:text-grey-1000'

function PaginationPrevious({
	className,
	...props
}: React.ComponentProps<typeof PaginationLink>) {
	return (
		<PaginationLink
			aria-label='Go to previous page'
			className={cn(ARROW_CLASS_NAME, className)}
			{...props}
		>
			<ChevronLeftIcon size={16} />
		</PaginationLink>
	)
}

function PaginationNext({
	className,
	...props
}: React.ComponentProps<typeof PaginationLink>) {
	return (
		<PaginationLink
			aria-label='Go to next page'
			className={cn(ARROW_CLASS_NAME, className)}
			{...props}
		>
			<ChevronRightIcon size={16} />
		</PaginationLink>
	)
}

function PaginationEllipsis({
	className,
	...props
}: React.ComponentProps<'span'>) {
	return (
		<span
			aria-hidden
			className={cn(
				// Matches the buttons' box so the row keeps an even rhythm.
				'mb-2 flex size-8 items-end justify-center text-grey-1000',
				className
			)}
			data-slot='pagination-ellipsis'
			{...props}
		>
			<DotsHorizontalIcon size={16} />
			<span className='sr-only'>More pages</span>
		</span>
	)
}

export {
	Pagination,
	PaginationContent,
	PaginationLink,
	PaginationItem,
	PaginationPrevious,
	PaginationNext,
	PaginationEllipsis
}
